import type {
  AnalysisMetadata,
  AnalysisProvenance,
  AnalysisRun,
  EngineVersions,
} from '../model.js';
import { activeEngines, engineVersionChips } from './engine-chips.js';
import { engineShortName } from './engine-names.js';
import { formatAge } from './queue-presentation.js';

/**
 * How one run is named when it sits in a list of runs of the same video.
 *
 * Ported from the legacy frontend's `runLabels.js`. The problem it solves is specific:
 * on the detail screen a video has several runs, they all analysed the same video, and
 * the operator has to pick one. «Run 1, Run 2, Run 3» does not help — what distinguishes
 * them is the batch, the configuration and the engine versions, so those are what the
 * label carries.
 */

/**
 * Prefix of the runs the backfill invented for results older than the ledger.
 *
 * They exist so old results are visible at all, but they have no engine versions, no
 * configuration and no stages — nothing was recorded when they ran. A screen that
 * offers to compare or replay one is offering something that cannot work, so the
 * distinction has to be legible.
 */
export const LEGACY_RUN_PREFIX = 'legacy-';

export function isLegacyRun(run: Pick<AnalysisRun, 'id' | 'trigger'> | null | undefined): boolean {
  if (!run) return true;

  return run.trigger === 'legacy' || !run.id || run.id.startsWith(LEGACY_RUN_PREFIX);
}

const TRIGGER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  upload: 'Subida',
  reanalyze: 'Reanálisis',
  bulk_reanalyze: 'Reanálisis en bloque',
  /** Upstream relaunched a failed video by itself, once. Nobody in the team launched it. */
  auto_retry: 'Reintento automático',
  group_rerun: 'Ejecución de grupo',
  http: 'API',
  replay: 'Repetición',
  legacy: 'Previo al versionado',
});

/**
 * What launched a run, in words. A trigger this build does not know reads as upstream spelled it:
 * still more useful than nothing.
 */
export function triggerLabel(trigger: string | null | undefined): string {
  if (!trigger) return '';

  return TRIGGER_LABELS[trigger] ?? trigger;
}

/** First characters of a hash: enough to tell two apart, short enough to read. */
export function shortHash(value: string | null | undefined, length = 8): string {
  if (!value) return '';

  return value.slice(0, length);
}

/** `config v3` when the version is known, else `config 9d1c2f3a`, else nothing. */
export function configLabel(run: Pick<AnalysisRun, 'config'> | null | undefined): string {
  const version = run?.config?.version;

  if (typeof version === 'number' && Number.isFinite(version) && version > 0) {
    return `config v${version}`;
  }

  const hash = shortHash(run?.config?.enginesHash);

  return hash ? `config ${hash}` : '';
}

/**
 * When a run happened.
 *
 * Finished, else started, else enqueued — the most definite instant available. Sorting
 * on `enqueuedAt` alone would order a run that waited an hour before a run that was
 * queued later and finished first, which is not the order anybody reads a history in.
 */
export function runTimestamp(run: AnalysisRun | null | undefined): Date | null {
  return run?.finishedAt ?? run?.startedAt ?? run?.enqueuedAt ?? null;
}

/**
 * Label for a run in a selector: `Ejecución 3 · Corpus@2026-09-23 · config v2 · hace 2 h`.
 *
 * Parts that are not known are left out rather than filled in. «config —» in a dropdown
 * reads as a configuration called «—».
 */
export function runLabel(run: AnalysisRun | null | undefined, now: number): string {
  if (!run) return '';

  const parts: string[] = [
    Number.isFinite(run.attempt) && run.attempt > 0 ? `Ejecución ${run.attempt}` : 'Ejecución',
  ];

  if (run.batch) parts.push(run.batch);

  const config = configLabel(run);
  if (config) parts.push(config);

  const age = formatAge(runTimestamp(run), now);
  if (age) parts.push(age);

  return parts.join(' · ');
}

const HIGHLIGHTED_ENGINES = ['gestures', 'coherence'] as const;

/**
 * One line naming a run and what it ran with.
 *
 * `Ejecución 3 · config v2 · Gestos 2.0.3 / Veracidad 1.4.2 · build 707a6d5`. The header
 * of the detail screen, where the question being answered is «what am I looking at» —
 * which is a question about versions, not about time.
 */
export function runHeadline(run: AnalysisRun | null | undefined): string {
  if (!run) return '';

  const parts: string[] = [
    Number.isFinite(run.attempt) && run.attempt > 0 ? `Ejecución ${run.attempt}` : 'Ejecución',
  ];

  const config = configLabel(run);
  if (config) parts.push(config);

  const engines = run.engine?.engines ?? {};
  const highlighted = HIGHLIGHTED_ENGINES.filter((name) => engines[name] != null).map(
    (name) => `${engineShortName(name)} ${engines[name]}`,
  );

  if (highlighted.length > 0) parts.push(highlighted.join(' / '));

  if (run.engine?.buildSha) parts.push(`build ${shortHash(run.engine.buildSha, 7)}`);

  // Said in the selector because nobody launched it: without it, a run appears in the list that
  // no one in the team remembers asking for.
  if (run.trigger === 'auto_retry') parts.push(triggerLabel(run.trigger));

  return parts.join(' · ');
}

/**
 * The order a selector lists runs in.
 *
 * Successful first, because that is what anybody is looking for; then what is still
 * going, then what failed. Newest first inside each group, and the higher attempt wins a
 * tie — two runs can share a timestamp when a batch was launched at once, and an order
 * that is not total renders differently on each fetch.
 */
const STATUS_ORDER: Readonly<Record<string, number>> = Object.freeze({
  SUCCEEDED: 0,
  RUNNING: 1,
  CANCEL_REQUESTED: 1,
  DISPATCHED: 2,
  QUEUED: 2,
  PAUSED: 2,
  FAILED: 3,
  CANCELLED: 4,
});

export function sortRunsForSelector(
  runs: readonly AnalysisRun[] | null | undefined,
): AnalysisRun[] {
  const list = (Array.isArray(runs) ? runs : []).filter(Boolean);

  return [...list].sort((a, b) => {
    const order = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
    if (order !== 0) return order;

    const time = (runTimestamp(b)?.getTime() ?? 0) - (runTimestamp(a)?.getTime() ?? 0);
    if (time !== 0) return time;

    return (b.attempt || 0) - (a.attempt || 0);
  });
}

/**
 * A chip of the review header.
 *
 * `tone` rather than a badge variant: the header decides how a tone looks, this only says what the
 * chip means.
 */
export interface RunChip {
  key: string;
  text: string;
  tone: 'neutral' | 'success' | 'info' | 'warning' | 'danger';
  title: string | null;
}

/**
 * The chips that say which run the review is showing.
 *
 * The legacy's `runVersionChips`, in Spanish: active, pinned, the engine versions, the
 * configuration with its short hash, the build, the batch, partial and degraded.
 *
 * A run from before the ledger has no engine block and often no configuration, so the document's
 * provenance stands in for both. Without it, 305 — configuration 3, version 5 — would show no
 * configuration at all, which reads as «ran with none».
 */
export function runHeaderChips(
  run: AnalysisMetadata['run'],
  provenance: AnalysisProvenance | null | undefined,
): RunChip[] {
  if (!run) return [];

  const chips: RunChip[] = [];
  const chip = (
    key: string,
    text: string,
    tone: RunChip['tone'] = 'neutral',
    title: string | null = null,
  ): void => {
    chips.push({ key, text, tone, title });
  };

  if (run.isActive) {
    /*
     * Upstream's pointer, and read-only here.
     *
     * The back-office no longer promotes or pins, because a video has the analyses it has and
     * none of them is the official one. The chip stays all the same: opening a review without a
     * run id serves whichever run Virtual Profiler considers active, so this is the only thing
     * that says «the one you are looking at by default is this one». Dropping it would leave a
     * history of identical-looking runs with nothing marking the one the screen opens on.
     */
    chip(
      'active',
      'Activa',
      'success',
      'Es la ejecución que abre la revisión por defecto y la que cuenta Virtual Profiler. El back-office cuenta la última completa, que puede ser otra.',
    );
  }
  // One chip per engine that ran: `lib/engine-chips.ts` has why there is no «+N motores».
  const engine = run.engine ?? provenance?.engine ?? null;
  for (const engineChip of engineVersionChips(engine?.engines, activeEngines(run))) {
    chip(`engine:${engineChip.key}`, engineChip.label);
  }

  const version = run.config?.version ?? provenance?.analysisConfigVersion ?? null;
  const hash = run.config?.enginesHash ?? provenance?.enginesHash ?? null;
  const short = shortHash(hash);

  if (version !== null || short) {
    chip(
      'config',
      [version !== null ? `configuración v${version}` : 'configuración', short]
        .filter(Boolean)
        .join(' · '),
      'neutral',
      hash ? `Huella de la configuración: ${hash}` : null,
    );
  }

  // A local build (`dev`) is a build like any other: where the engine runs has nothing to do with
  // how well it analyses (the product owner, 2026-09-25).
  if (engine?.buildSha) chip('build', `build ${shortHash(engine.buildSha, 7)}`);

  if (run.batch) chip('batch', run.batch, 'neutral', 'Lote');

  if (run.partial) {
    const skipped = run.enginesSkipped.map((skip) => skip.name);

    chip(
      'partial',
      'parcial',
      'warning',
      skipped.length > 0
        ? `Se saltó algún motor: ${skipped.join(', ')}.`
        : 'Algún motor no llegó a ejecutarse.',
    );
  }

  const reasons = degradationOf(engine, engine?.degraded === true || provenance?.degraded === true);

  if (reasons) {
    // A warning, as in the queue: a degraded run finished. Red on this screen means «no result».
    chip(
      'degraded',
      'degradado',
      'warning',
      reasons.length > 0
        ? `Degradada: ${reasons.join(', ')}.`
        : 'Algún motor usó un modelo o un valor de reserva.',
    );
  }

  return chips;
}

/**
 * Degraded reasons that never made an analysis worse.
 *
 * `build_sha_dev` called every run of a local build degraded. Where the engine runs has nothing to
 * do with how well it analyses, and coherence 3.1.0 stopped recording it; the runs recorded before
 * still carry it, so it is left out here.
 */
const NOT_DEGRADING: ReadonlySet<string> = new Set(['build_sha_dev']);

/**
 * Why a run is degraded — its reasons and its silent fallbacks —, or null when it is not.
 *
 * `degraded` is upstream's flag, which the caller reads from wherever the run keeps it. Empty when
 * upstream says degraded without saying why; null when every reason it gave never degraded
 * anything.
 */
export function degradationOf(
  engine: Pick<EngineVersions, 'degradedReasons' | 'silentFallbacks'> | null | undefined,
  degraded: boolean,
): string[] | null {
  if (!degraded) return null;

  const given = [
    ...(engine?.degradedReasons ?? []),
    ...(engine?.silentFallbacks ?? []).map((fallback) => fallback.name),
  ];
  const reasons = given.filter((reason) => !NOT_DEGRADING.has(reason));

  return given.length > 0 && reasons.length === 0 ? null : reasons;
}

/** Why an engine block is degraded by its own flag, or null — the worker's on the hub and groups. */
export function engineDegradation(
  engine:
    Pick<EngineVersions, 'degraded' | 'degradedReasons' | 'silentFallbacks'> | null | undefined,
): string[] | null {
  return degradationOf(engine, engine?.degraded === true);
}
